import { pathReturnHref } from "../learning/journeyModel";
import { browserPracticeTimezone } from "../puzzles/dailyPractice";
import { refreshLearning } from "../learning/learningEvents";
import { recordGuestActivity } from "../learning/guestLearningProgress";
import { useEffect, useMemo, useRef, useState } from "react";
// import Board from "../../chessBoard3/components/Board";
import Board from "../../chessBoard/components/Board";
import { Chess } from "@chesshotel/chess";
import { api } from "@/services/api";
import { useUser } from "@/redux/user";
import {
  getGuestLessonProgress,
  saveGuestLessonProgress,
} from "@/modules/lessons/guestLessonProgress";

// import { Chess } from "./chess";
import { convertMoves } from "../../util/moves";
import { cn } from "@/lib/utils";
import { uci2move } from "@chesshotel/gameReview";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSettings } from "@/redux/settings";
import { openingLessonMetadata } from "@chesshotel/types";
import { useRouter } from "next/router";

import {
  FaStepBackward,
  FaStepForward,
  FaChevronDown,
  FaUndo,
} from "react-icons/fa";
import Link from "next/link";

import useTranslation from "next-translate/useTranslation";
import {
  LessonPanelHeader,
  LessonPanelFooter,
  LessonBoardFrame,
  lessonPanelClassName,
  lessonPanelStyle,
  lessonPrimaryClassName,
  lessonHintClassName,
  lessonWorkspaceClassName,
  useLessonBoardSize,
} from "./LessonWorkspace";

// import Puzzle from "@/pages/puzzle";
// const chess = Chess();

enum LessonState {
  OPENING,
  IN_PROGRESS,
  DONE,
  CHALLENGE,
  CHALLENGE_DONE,
}

type StartMode = "practice" | "challenge";

type LessonProgressResponse = {
  completedLines?: unknown;
  completedChallengeLines?: unknown;
  practiceLineProgress?: unknown;
  challengeLineProgress?: unknown;
  challengeCompleted?: boolean;
};

type PersistProgressArgs = {
  completedLine?: { index: number; mode: "practice" | "challenge" };
  completedLinesOverride?: number[];
  completedChallengeLinesOverride?: number[];
  practiceLineProgressOverride?: number[];
  challengeLineProgressOverride?: number[];
  challengeCompletedOverride?: boolean;
};

const normalizeCompletedIndices = (value: unknown): number[] => {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((entry) => Number(entry))
          .filter((entry) => Number.isInteger(entry) && entry >= 0),
      ),
    );
  }

  if (typeof value !== "string" || value.trim() === "") {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return Array.from(
        new Set(
          parsed
            .map((entry) => Number(entry))
            .filter((entry) => Number.isInteger(entry) && entry >= 0),
        ),
      );
    }
  } catch (_error) {
    // fall through to CSV parsing
  }

  return Array.from(
    new Set(
      value
        .split(",")
        .map((entry) => Number(entry.trim()))
        .filter((entry) => Number.isInteger(entry) && entry >= 0),
    ),
  );
};

const parseNumberArray = (value: unknown): number[] => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => Number(entry))
      .filter((entry) => Number.isInteger(entry) && entry >= 0);
  }

  if (typeof value !== "string" || value.trim() === "") {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed
        .map((entry) => Number(entry))
        .filter((entry) => Number.isInteger(entry) && entry >= 0);
    }
  } catch (_error) {
    // fall through to CSV parsing
  }

  return value
    .split(",")
    .map((entry) => Number(entry.trim()))
    .filter((entry) => Number.isInteger(entry) && entry >= 0);
};

const normalizeLineProgress = (value: unknown): number[] => {
  const normalized = parseNumberArray(value);

  let lastNonZeroIndex = normalized.length - 1;
  while (lastNonZeroIndex >= 0 && normalized[lastNonZeroIndex] === 0) {
    lastNonZeroIndex -= 1;
  }

  if (lastNonZeroIndex < 0) {
    return [];
  }

  return normalized.slice(0, lastNonZeroIndex + 1);
};

const getPlayerMoveCountFromPly = (
  plyCount: number,
  playingWhite: boolean,
): number => {
  if (plyCount <= 0) {
    return 0;
  }

  return playingWhite ? Math.ceil(plyCount / 2) : Math.floor(plyCount / 2);
};

const getLinePlayerMoveTotals = (lessonLines: any[], playingWhite: boolean) => {
  return (lessonLines || []).map((lessonLine) => {
    if (!lessonLine?.moves) {
      return 0;
    }

    try {
      const chess = new Chess();
      chess.loadPgn(lessonLine.moves);
      const lineMoves = chess.history({ verbose: true });
      return getPlayerMoveCountFromPly(lineMoves.length, playingWhite);
    } catch (_error) {
      return Math.max((lessonLine?.comments?.length ?? 1) - 1, 0);
    }
  });
};

const clampPercent = (value: number) => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.min(Math.max(value, 0), 100);
};

function LessonProgressBar({
  value = 0,
  label,
  className = "",
  indicatorClassName = "bg-primary",
}: {
  value?: number;
  label: string;
  className?: string;
  indicatorClassName?: string;
}) {
  const clampedValue = clampPercent(value);

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={clampedValue}
      className={`relative h-2 w-full overflow-hidden rounded-full bg-athens-gray-200 ${className}`.trim()}
    >
      <div
        className={`h-full w-full flex-1 transition-all ${indicatorClassName}`.trim()}
        style={{ transform: `translateX(-${100 - clampedValue}%)` }}
      />
    </div>
  );
}

const setLineProgressAtIndex = (
  sourceProgress: number[],
  targetLineIndex: number,
  moveCount: number,
) => {
  const normalized = normalizeLineProgress(sourceProgress);
  const nextProgress = [...normalized];
  const currentValue = nextProgress[targetLineIndex] ?? 0;
  nextProgress[targetLineIndex] = Math.max(currentValue, moveCount);
  return normalizeLineProgress(nextProgress);
};

const applyCompletedLinesToProgress = (
  sourceProgress: number[],
  completedLineIndices: number[],
  lineMoveTotals: number[],
) => {
  const nextProgress = [...normalizeLineProgress(sourceProgress)];

  completedLineIndices.forEach((lineIdx) => {
    const lineTotal = lineMoveTotals[lineIdx] ?? 0;
    if (lineTotal > 0) {
      nextProgress[lineIdx] = Math.max(nextProgress[lineIdx] ?? 0, lineTotal);
    }
  });

  return normalizeLineProgress(nextProgress);
};

const findNextIncompleteLine = (
  lineProgress: number[],
  lineMoveTotals: number[],
) => {
  return lineMoveTotals.findIndex((lineTotal, lineIdx) => {
    const completedMoves = Math.min(lineProgress[lineIdx] ?? 0, lineTotal);
    return completedMoves < lineTotal;
  });
};

const areAllLinesCompleted = (
  lineProgress: number[],
  completedLineIndices: number[],
  lineMoveTotals: number[],
) => {
  if (!lineMoveTotals || lineMoveTotals.length === 0) {
    return false;
  }

  const completedSet = new Set(completedLineIndices);

  return lineMoveTotals.every((lineTotal, lineIdx) => {
    if (completedSet.has(lineIdx)) {
      return true;
    }

    if (lineTotal <= 0) {
      return true;
    }

    return (lineProgress[lineIdx] ?? 0) >= lineTotal;
  });
};

const getMoveIndexForResume = (
  playingWhite: boolean,
  playerMovesDone: number,
) => {
  if (playerMovesDone <= 0) {
    return 0;
  }

  return playingWhite ? playerMovesDone * 2 : playerMovesDone * 2 + 1;
};
// const startFen = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

export default function LessonBoard(props) {
  const { t } = useTranslation("common");
  const { locale, query } = useRouter();
  const {
    playingWhite,
    lines,
    introText,
    title,
    challengeFen,
    challengeOpeningLastMove,
    challengeLines,
    lessonId,
  } = props;

  const user = useUser();
  const settings = useSettings();
  const { size: boardSize } = useLessonBoardSize();

  // const [positionIndex, setPositionIndex] = useState(0);

  const [moveIndex, setMoveIndex] = useState(0);
  const [lineIndex, setLineIndex] = useState(0);
  const [maxMoveIndex, setMaxMoveIndex] = useState(0);
  const [moveStatus, setMoveStatus] = useState<"correct" | "incorrect" | null>(
    null,
  );
  const [playerMovesMade, setPlayerMovesMade] = useState(moveIndex / 2);

  const [isManualNavigation, setIsManualNavigation] = useState(false);
  const [completedLines, setCompletedLines] = useState<number[]>([]);
  const [completedChallengeLines, setCompletedChallengeLines] = useState<
    number[]
  >([]);
  const [practiceLineProgress, setPracticeLineProgress] = useState<number[]>(
    [],
  );
  const [challengeLineProgress, setChallengeLineProgress] = useState<number[]>(
    [],
  );

  const [moves, setMoves] = useState<any[]>([]);
  const [possibleMovesArr, setPossibleMovesArr] = useState([]);
  const [fen, setFen] = useState(() => new Chess().fen());
  const [lastMove, setLastMove] = useState(null);
  // const positionIndex = useRef(0);
  const [lessonState, setLessonState] = useState(LessonState.OPENING);
  const [startMode, setStartMode] = useState<StartMode>("practice");
  const [highLightKey, setHighLightKey] = useState(null);
  const [boardAnimate, setBoardAnimate] = useState(true);
  const persistQueueRef = useRef<Promise<void>>(Promise.resolve());

  const chess = useRef(new Chess()).current;

  const isChallengeLessonState =
    lessonState === LessonState.CHALLENGE ||
    lessonState === LessonState.CHALLENGE_DONE;
  // Challenge positions are authored separately; do not repeat guided lines as
  // additional challenges. Their indices match saved progress and the manifest.
  const normalizedChallengeLines = useMemo(
    () => (Array.isArray(challengeLines) ? challengeLines : []),
    [challengeLines],
  );
  const currentLines = isChallengeLessonState
    ? normalizedChallengeLines
    : lines;
  const line = currentLines?.[lineIndex];
  const maxLines = currentLines?.length || 0;
  const hasChallenge = normalizedChallengeLines.length > 0;
  const isOpening = lessonState === LessonState.OPENING;
  const isPracticeInProgress = lessonState === LessonState.IN_PROGRESS;
  const isChallengeInProgress = lessonState === LessonState.CHALLENGE;
  const isInProgressState = isPracticeInProgress || isChallengeInProgress;
  const practiceLineCount = lines?.length ?? 0;
  const challengeLineCount = normalizedChallengeLines.length;
  const selectedStartLinesLength =
    startMode === "challenge" && hasChallenge
      ? challengeLineCount
      : practiceLineCount;
  const lineCountTotal = isOpening ? selectedStartLinesLength : maxLines;
  const safeLineIndex =
    lineCountTotal > 0 ? Math.min(lineIndex, lineCountTotal - 1) : 0;
  const safePracticeLineIndex =
    practiceLineCount > 0 ? Math.min(lineIndex, practiceLineCount - 1) : 0;
  const safeChallengeLineIndex =
    challengeLineCount > 0 ? Math.min(lineIndex, challengeLineCount - 1) : 0;

  const commentIndex = playingWhite
    ? Math.ceil(moveIndex / 2)
    : Math.floor(moveIndex / 2);
  const ctaButtonClassName = lessonPrimaryClassName;
  const practiceLineMoveTotals = useMemo(
    () => getLinePlayerMoveTotals(lines, playingWhite),
    [lines, playingWhite],
  );
  const challengeLineMoveTotals = useMemo(
    () => getLinePlayerMoveTotals(normalizedChallengeLines, playingWhite),
    [normalizedChallengeLines, playingWhite],
  );
  const currentLineTotalMoves = Math.max(
    isChallengeLessonState
      ? (challengeLineMoveTotals[lineIndex] ?? 0)
      : (practiceLineMoveTotals[lineIndex] ?? 0),
    1,
  );
  const effectiveCompletedPracticeLines = useMemo(() => {
    if (lessonState !== LessonState.DONE) {
      return completedLines;
    }

    return Array.from(new Set([...completedLines, lineIndex]));
  }, [lessonState, completedLines, lineIndex]);
  const effectiveCompletedChallengeLines = useMemo(() => {
    if (lessonState !== LessonState.CHALLENGE_DONE) {
      return completedChallengeLines;
    }

    return Array.from(new Set([...completedChallengeLines, lineIndex]));
  }, [lessonState, completedChallengeLines, lineIndex]);
  const effectivePracticeLineProgress = useMemo(() => {
    if (lessonState !== LessonState.DONE) {
      return practiceLineProgress;
    }

    const completedMoves = practiceLineMoveTotals[lineIndex] ?? 0;
    return setLineProgressAtIndex(
      practiceLineProgress,
      lineIndex,
      completedMoves,
    );
  }, [lessonState, practiceLineProgress, lineIndex, practiceLineMoveTotals]);
  const effectiveChallengeLineProgress = useMemo(() => {
    if (lessonState !== LessonState.CHALLENGE_DONE) {
      return challengeLineProgress;
    }

    const completedMoves = challengeLineMoveTotals[lineIndex] ?? 0;
    return setLineProgressAtIndex(
      challengeLineProgress,
      lineIndex,
      completedMoves,
    );
  }, [lessonState, challengeLineProgress, lineIndex, challengeLineMoveTotals]);
  const allPracticeLinesCompleted = useMemo(
    () =>
      areAllLinesCompleted(
        effectivePracticeLineProgress,
        effectiveCompletedPracticeLines,
        practiceLineMoveTotals,
      ),
    [
      effectivePracticeLineProgress,
      effectiveCompletedPracticeLines,
      practiceLineMoveTotals,
    ],
  );
  const allChallengeLinesCompleted = useMemo(
    () =>
      areAllLinesCompleted(
        effectiveChallengeLineProgress,
        effectiveCompletedChallengeLines,
        challengeLineMoveTotals,
      ),
    [
      effectiveChallengeLineProgress,
      effectiveCompletedChallengeLines,
      challengeLineMoveTotals,
    ],
  );
  const isChallengeUnlocked = !hasChallenge || allPracticeLinesCompleted;

  let comment = line?.comments?.[commentIndex];

  const isWhiteTurn = moveIndex % 2 === 0;
  const opponentTurn =
    (playingWhite && !isWhiteTurn) || (!playingWhite && isWhiteTurn);

  if (opponentTurn && isInProgressState) {
    comment = isWhiteTurn ? t("White moves.") : t("Black moves.");
  }
  if (lessonState === LessonState.OPENING) {
    comment = introText;
  }

  // Allow moving any legal piece on the player's turn.
  const possibleMoves =
    lessonState === LessonState.OPENING
      ? {}
      : possibleMovesArr[moveIndex] || {};

  // console.log("moveIndex", moveIndex);
  // console.log("possibleMovesArr", possibleMovesArr);
  // console.log("possibleMoves", possibleMoves);

  //console.log("positionIndex", positionIndex);
  // console.log("maxMoveIndex", maxMoveIndex);

  async function persistProgress({
    completedLine,
    completedLinesOverride = completedLines,
    completedChallengeLinesOverride = completedChallengeLines,
    practiceLineProgressOverride = practiceLineProgress,
    challengeLineProgressOverride = challengeLineProgress,
    challengeCompletedOverride,
  }: PersistProgressArgs = {}) {
    if (!lessonId) {
      return;
    }

    const normalizedPracticeLineProgress = applyCompletedLinesToProgress(
      normalizeLineProgress(practiceLineProgressOverride),
      normalizeCompletedIndices(completedLinesOverride),
      practiceLineMoveTotals,
    );
    const normalizedChallengeLineProgress = applyCompletedLinesToProgress(
      normalizeLineProgress(challengeLineProgressOverride),
      normalizeCompletedIndices(completedChallengeLinesOverride),
      challengeLineMoveTotals,
    );

    const completedPracticeLineSet = new Set(
      normalizeCompletedIndices(completedLinesOverride),
    );
    const completedChallengeLineSet = new Set(
      normalizeCompletedIndices(completedChallengeLinesOverride),
    );

    normalizedPracticeLineProgress.forEach((movesDone, lineIdx) => {
      const lineTotal = practiceLineMoveTotals[lineIdx] ?? 0;
      if (lineTotal > 0 && movesDone >= lineTotal) {
        completedPracticeLineSet.add(lineIdx);
      }
    });

    normalizedChallengeLineProgress.forEach((movesDone, lineIdx) => {
      const lineTotal = challengeLineMoveTotals[lineIdx] ?? 0;
      if (lineTotal > 0 && movesDone >= lineTotal) {
        completedChallengeLineSet.add(lineIdx);
      }
    });

    const normalizedCompletedLines = Array.from(completedPracticeLineSet).sort(
      (a, b) => a - b,
    );
    const normalizedCompletedChallengeLines = Array.from(
      completedChallengeLineSet,
    ).sort((a, b) => a - b);

    const challengeCompleted =
      challengeCompletedOverride ??
      (hasChallenge &&
        findNextIncompleteLine(
          normalizedChallengeLineProgress,
          challengeLineMoveTotals,
        ) === -1);

    const payload = {
      lessonId,
      timezone: browserPracticeTimezone(),
      completedLine,
      completedLines: normalizedCompletedLines,
      completedChallengeLines: normalizedCompletedChallengeLines,
      practiceLineProgress: normalizedPracticeLineProgress,
      challengeLineProgress: normalizedChallengeLineProgress,
      challengeCompleted,
    };

    persistQueueRef.current = persistQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        try {
          if (user.memberLoggedIn) {
            await api.post("/lesson-progress", payload);
            if (completedLine) refreshLearning();
          } else {
            saveGuestLessonProgress({
              lessonId,
              completedLines: normalizedCompletedLines,
              completedChallengeLines: normalizedCompletedChallengeLines,
              practiceLineProgress: normalizedPracticeLineProgress,
              challengeLineProgress: normalizedChallengeLineProgress,
              challengeCompleted: Boolean(challengeCompleted),
            });
            if (completedLine)
              recordGuestActivity(
                "lesson",
                `${lessonId}:${completedLine.mode}:${completedLine.index}`,
              );
          }

          setCompletedLines(normalizedCompletedLines);
          setCompletedChallengeLines(normalizedCompletedChallengeLines);
          setPracticeLineProgress(normalizedPracticeLineProgress);
          setChallengeLineProgress(normalizedChallengeLineProgress);
        } catch (error) {
          console.error("Failed to save lesson progress:", error);
        }
      });

    await persistQueueRef.current;
  }

  async function loadProgress() {
    if (!lessonId) {
      return;
    }

    try {
      const progress = user.memberLoggedIn
        ? ((await api.get(`/lesson-progress/${lessonId}`))
            .data as LessonProgressResponse)
        : (getGuestLessonProgress(lessonId) as LessonProgressResponse | null);

      if (progress) {
        const parsedCompletedLines = normalizeCompletedIndices(
          progress.completedLines,
        );
        const parsedCompletedChallengeLines = progress.challengeCompleted
          ? normalizedChallengeLines.map((_, i) => i)
          : normalizeCompletedIndices(progress.completedChallengeLines);
        const parsedPracticeLineProgress = normalizeLineProgress(
          progress.practiceLineProgress,
        );
        const parsedChallengeLineProgress = normalizeLineProgress(
          progress.challengeLineProgress,
        );

        const hydratedPracticeLineProgress = applyCompletedLinesToProgress(
          parsedPracticeLineProgress,
          parsedCompletedLines,
          practiceLineMoveTotals,
        );
        const hydratedChallengeLineProgress = applyCompletedLinesToProgress(
          parsedChallengeLineProgress,
          parsedCompletedChallengeLines,
          challengeLineMoveTotals,
        );

        setCompletedLines(parsedCompletedLines);
        setCompletedChallengeLines(parsedCompletedChallengeLines);
        setPracticeLineProgress(hydratedPracticeLineProgress);
        setChallengeLineProgress(hydratedChallengeLineProgress);

        const nextPracticeLine = findNextIncompleteLine(
          hydratedPracticeLineProgress,
          practiceLineMoveTotals,
        );
        const practiceCompleted = nextPracticeLine === -1;
        const nextChallengeLine = findNextIncompleteLine(
          hydratedChallengeLineProgress,
          challengeLineMoveTotals,
        );
        const challengeCompleted = nextChallengeLine === -1;

        if (practiceCompleted && hasChallenge && !challengeCompleted) {
          setStartMode("challenge");
          setLineIndex(nextChallengeLine === -1 ? 0 : nextChallengeLine);
        } else {
          setStartMode("practice");
          setLineIndex(nextPracticeLine === -1 ? 0 : nextPracticeLine);
        }

        setPlayerMovesMade(0);
        setLessonState(LessonState.OPENING);
      }
    } catch (error) {
      console.error("Failed to load lesson progress:", error);
    }
  }

  async function saveProgress() {
    const updatedPracticeLineProgress = [...practiceLineProgress];
    const updatedChallengeLineProgress = [...challengeLineProgress];
    const updatedCompletedLines = [...completedLines];
    const updatedCompletedChallengeLines = [...completedChallengeLines];

    if (lessonState === LessonState.DONE) {
      const lineTotal = practiceLineMoveTotals[lineIndex] ?? 0;
      if (lineTotal > 0) {
        updatedPracticeLineProgress[lineIndex] = Math.max(
          updatedPracticeLineProgress[lineIndex] ?? 0,
          lineTotal,
        );
      }
      updatedCompletedLines.push(lineIndex);
    } else if (lessonState === LessonState.CHALLENGE_DONE) {
      const lineTotal = challengeLineMoveTotals[lineIndex] ?? 0;
      if (lineTotal > 0) {
        updatedChallengeLineProgress[lineIndex] = Math.max(
          updatedChallengeLineProgress[lineIndex] ?? 0,
          lineTotal,
        );
      }
      updatedCompletedChallengeLines.push(lineIndex);
    }

    await persistProgress({
      completedLine:
        lessonState === LessonState.DONE
          ? { index: lineIndex, mode: "practice" }
          : lessonState === LessonState.CHALLENGE_DONE
            ? { index: lineIndex, mode: "challenge" }
            : undefined,
      completedLinesOverride: updatedCompletedLines,
      completedChallengeLinesOverride: updatedCompletedChallengeLines,
      practiceLineProgressOverride: updatedPracticeLineProgress,
      challengeLineProgressOverride: updatedChallengeLineProgress,
    });
  }

  function initLine() {
    const chess = new Chess();
    const currentLines = isChallengeLessonState
      ? normalizedChallengeLines
      : lines;
    const line = currentLines?.[lineIndex];
    if (!line) return;
    chess.loadPgn(line.moves);
    const moves = chess.history({ verbose: true });

    chess.reset();

    // For challenge mode, load the challenge FEN if it exists.
    // For practice mode, always start from the regular starting position.
    if (isChallengeLessonState && challengeFen) {
      chess.load(challengeFen);
    }

    const possibleMovesArr = [];

    possibleMovesArr.push(convertMoves(chess.moves({ verbose: true })));

    moves.forEach((move, index) => {
      chess.move(move);
      let possibleMoves;
      if (playingWhite) {
        possibleMoves =
          index % 2 === 1 ? convertMoves(chess.moves({ verbose: true })) : {};
      } else {
        possibleMoves =
          index % 2 === 1 ? {} : convertMoves(chess.moves({ verbose: true }));
      }
      possibleMovesArr.push(possibleMoves);
    });

    setMoves(moves);
    setPossibleMovesArr(possibleMovesArr);
  }

  function startLesson(targetLineIndex = lineIndex, resumePlayerMoves = 0) {
    setBoardAnimate(false);
    chess.reset();
    const lineTotal = practiceLineMoveTotals[targetLineIndex] ?? 0;
    const clampedPlayerMoves = Math.max(
      0,
      Math.min(resumePlayerMoves, lineTotal),
    );
    const startingMoveIndex = getMoveIndexForResume(
      playingWhite,
      clampedPlayerMoves,
    );

    setFen(chess.fen());
    setLastMove(null);
    setMoves([]);
    setPossibleMovesArr([]);
    setMoveIndex(startingMoveIndex);
    setMaxMoveIndex(startingMoveIndex);
    setMoveStatus(null);
    setPlayerMovesMade(clampedPlayerMoves);
    setLineIndex(targetLineIndex);
    setStartMode("practice");
    setLessonState(LessonState.IN_PROGRESS);
  }

  function startChallenge(targetLineIndex = lineIndex, resumePlayerMoves = 0) {
    if (normalizedChallengeLines.length === 0) {
      console.error("No challenge lines defined");
      return;
    }
    setBoardAnimate(false);
    const lineTotal = challengeLineMoveTotals[targetLineIndex] ?? 0;
    const clampedPlayerMoves = Math.max(
      0,
      Math.min(resumePlayerMoves, lineTotal),
    );
    const startingMoveIndex = getMoveIndexForResume(
      playingWhite,
      clampedPlayerMoves,
    );

    chess.reset();
    if (challengeFen) {
      chess.load(challengeFen);
    }

    setFen(chess.fen());
    setLastMove(
      challengeOpeningLastMove ? uci2move(challengeOpeningLastMove) : null,
    );
    setMoveIndex(startingMoveIndex);
    setMaxMoveIndex(startingMoveIndex);
    setLineIndex(targetLineIndex);
    setMoves([]);
    setPossibleMovesArr([]);
    setMoveStatus(null);
    setPlayerMovesMade(clampedPlayerMoves);
    setStartMode("challenge");
    setLessonState(LessonState.CHALLENGE);
  }

  function resetToOpening(nextStartMode: StartMode) {
    setBoardAnimate(false);
    chess.reset();
    setStartMode(nextStartMode);
    setLessonState(LessonState.OPENING);
    setFen(chess.fen());
    setLastMove(null);
    setMoves([]);
    setPossibleMovesArr([]);
    setMoveIndex(0);
    setMaxMoveIndex(0);
    setMoveStatus(null);
    setPlayerMovesMade(0);
    setIsManualNavigation(false);
  }

  function getResumableMovesForLine(
    mode: StartMode,
    targetLineIndex: number,
  ): number {
    const isChallengeMode = mode === "challenge" && hasChallenge;
    const lineProgress = isChallengeMode
      ? challengeLineProgress
      : practiceLineProgress;
    const lineMoveTotals = isChallengeMode
      ? challengeLineMoveTotals
      : practiceLineMoveTotals;
    const lineTotal = lineMoveTotals[targetLineIndex] ?? 0;
    const savedMoves = Math.min(lineProgress[targetLineIndex] ?? 0, lineTotal);

    if (savedMoves <= 0 || savedMoves >= lineTotal) {
      return 0;
    }

    return savedMoves;
  }

  function changeLine(nextLineIndex: number) {
    if (lineCountTotal <= 0) {
      return;
    }

    const clampedLineIndex = Math.max(
      0,
      Math.min(nextLineIndex, lineCountTotal - 1),
    );

    if (clampedLineIndex === lineIndex) {
      return;
    }

    setLineIndex(clampedLineIndex);
    resetToOpening(startMode);
  }

  function startPracticeFromOpening() {
    const resumePlayerMoves = getResumableMovesForLine(
      "practice",
      safePracticeLineIndex,
    );
    startLesson(safePracticeLineIndex, resumePlayerMoves);
  }

  function startChallengeFromOpening() {
    if (!hasChallenge || !isChallengeUnlocked) {
      return;
    }

    const resumePlayerMoves = getResumableMovesForLine(
      "challenge",
      safeChallengeLineIndex,
    );
    startChallenge(safeChallengeLineIndex, resumePlayerMoves);
  }

  function getNextUncompletedChallengeLine() {
    const nextChallengeLine = findNextIncompleteLine(
      challengeLineProgress,
      challengeLineMoveTotals,
    );
    return nextChallengeLine === -1 ? 0 : nextChallengeLine;
  }

  // function updatePositionIndex(index: number) {
  //   positionIndex.current = index;

  //   const move = moves[positionIndex.current];
  //   // move && setFen(move.before);
  //   setMoveIndex(positionIndex.current);
  // }

  // useEffect(() => {
  //   positionIndex.current = moveIndex;
  // }, [moveIndex]);

  useEffect(() => {
    loadProgress();
  }, [user.memberLoggedIn, lessonId]);

  useEffect(() => {
    initLine();
  }, [lineIndex, lessonState]);

  useEffect(() => {
    if (lineCountTotal <= 0) {
      return;
    }

    if (lineIndex >= lineCountTotal) {
      setLineIndex(lineCountTotal - 1);
    }
  }, [lineCountTotal, lineIndex]);

  useEffect(() => {
    if (boardAnimate) {
      return;
    }

    const timer = setTimeout(() => {
      setBoardAnimate(true);
    }, 0);

    return () => clearTimeout(timer);
  }, [boardAnimate]);

  useEffect(() => {
    if (lessonState === LessonState.OPENING) return;
    if (!moves || moves.length === 0) return;

    // Show position after the previous move (includes castling with both king and rook)
    const fen =
      moveIndex === 0
        ? lessonState === LessonState.CHALLENGE && challengeFen
          ? challengeFen
          : moves[0]?.before
        : moves[moveIndex - 1]?.after;

    const move = moves?.[moveIndex - 1];
    if (fen) {
      setFen(fen);
    }
    setLastMove(move);
  }, [moveIndex, moves, lessonState, challengeFen]);

  useEffect(() => {
    if (
      lessonState === LessonState.DONE ||
      lessonState === LessonState.CHALLENGE_DONE
    ) {
      saveProgress();
    }
  }, [lessonState]);

  // Auto-play opponent moves
  useEffect(() => {
    if (
      lessonState !== LessonState.IN_PROGRESS &&
      lessonState !== LessonState.CHALLENGE
    )
      return;
    if (!moves || moves.length === 0) return;
    if (moveStatus !== null) return; // Don't auto-play during feedback
    if (isManualNavigation) return; // Don't auto-play during manual navigation

    const isWhiteTurn = moveIndex % 2 === 0;
    const opponentTurn =
      (playingWhite && !isWhiteTurn) || (!playingWhite && isWhiteTurn);

    if (opponentTurn && moveIndex < moves.length) {
      const timer = setTimeout(() => {
        setMoveIndex(moveIndex + 1);
        setMaxMoveIndex(moveIndex + 1);
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [
    moveIndex,
    lessonState,
    moves,
    playingWhite,
    moveStatus,
    isManualNavigation,
  ]);

  function moveMade(move) {
    if (!move) {
      console.error("moveMade called with undefined move");
      return;
    }
    if (!moves || moves.length === 0) {
      return;
    }
    setHighLightKey(null);
    setIsManualNavigation(false);

    const correctMove = moves[moveIndex];

    if (!correctMove) {
      return;
    }

    if (
      move.from === correctMove.from &&
      move.to === correctMove.to &&
      move.promotion === correctMove.promotion
    ) {
      if (isChallengeInProgress) {
        setMoveStatus("correct");
      } else {
        setMoveStatus(null);
      }
      const nextPlayerMovesMade = Math.ceil((moveIndex + 1) / 2);
      setPlayerMovesMade(nextPlayerMovesMade);

      if (lessonState === LessonState.CHALLENGE) {
        const updatedChallengeLineProgress = setLineProgressAtIndex(
          challengeLineProgress,
          lineIndex,
          nextPlayerMovesMade,
        );
        setChallengeLineProgress(updatedChallengeLineProgress);
        void persistProgress({
          challengeLineProgressOverride: updatedChallengeLineProgress,
        });
      } else if (lessonState === LessonState.IN_PROGRESS) {
        const updatedPracticeLineProgress = setLineProgressAtIndex(
          practiceLineProgress,
          lineIndex,
          nextPlayerMovesMade,
        );
        setPracticeLineProgress(updatedPracticeLineProgress);
        void persistProgress({
          practiceLineProgressOverride: updatedPracticeLineProgress,
        });
      }

      if (moves.length === moveIndex + 1) {
        setMoveIndex(moveIndex + 1);
        setMaxMoveIndex(moveIndex + 1);
        const newState =
          lessonState === LessonState.CHALLENGE
            ? LessonState.CHALLENGE_DONE
            : LessonState.DONE;
        setLessonState(newState);
        return;
      } else {
        // Increment moveIndex to show your move
        setMoveIndex(moveIndex + 1);
        setMaxMoveIndex(moveIndex + 1);

        if (isChallengeInProgress) {
          setTimeout(() => {
            setMoveStatus(null);
          }, 800);
        }
      }
    } else {
      setMoveStatus("incorrect");

      // Don't set FEN here - let the useEffect handle it
      // Just trigger a re-render and clear the error after delay
      setTimeout(() => {
        setFen(""); // Clear FEN first
        setTimeout(() => {
          // Then set correct FEN after a brief delay
          const correctFen =
            moveIndex === 0
              ? lessonState === LessonState.CHALLENGE && challengeFen
                ? challengeFen
                : moves[0]?.before
              : moves[moveIndex - 1]?.after;
          setFen(correctFen);
          setMoveStatus(null);
        }, 10);
      }, 500);
    }
  }

  function renderChallengeFeedbackCard() {
    if (!isChallengeInProgress) {
      return null;
    }

    const isCorrectMove = moveStatus === "correct";
    const isIncorrectMove = moveStatus === "incorrect";
    const feedbackText = isCorrectMove
      ? t("Keep going!")
      : isIncorrectMove
        ? t("Try again")
        : t("Play the {{opening}}", { opening: title });

    return (
      <div className="flex items-start w-full pb-2 mx-auto md:pb-4">
        <div
          className="w-full p-3 bg-white rounded-xl md:p-4"
          data-lesson-instruction
          aria-live="polite"
        >
          <p className="text-[15px] leading-7 text-slate-800">{feedbackText}</p>
        </div>
      </div>
    );
  }

  function renderCommentBubble() {
    if (isChallengeInProgress) return null;
    return (
      <div
        className="w-full rounded-xl bg-white p-4 text-[15px] leading-7 text-slate-800"
        data-lesson-instruction
        aria-live="polite"
      >
        {lessonState === LessonState.CHALLENGE_DONE ? (
          <>
            <h2 className="text-lg font-semibold">
              {t("Challenge Complete!")}
            </h2>
            <p className="mt-2">
              {t(
                allChallengeLinesCompleted
                  ? "You've mastered all challenge lines!"
                  : "You've completed this challenge line",
              )}
            </p>
          </>
        ) : (
          comment
        )}
      </div>
    );
  }

  function renderProgressBar() {
    const progressPercent =
      currentLineTotalMoves > 0
        ? (playerMovesMade / currentLineTotalMoves) * 100
        : 0;

    return (
      <>
        <div className="mb-1 text-xs text-gray-600">
          {/* {lessonState === LessonState.CHALLENGE ? t("Challenge ") : t("Practice ")} */}
          {t("Moves")} {playerMovesMade}/{currentLineTotalMoves}
        </div>
        <LessonProgressBar label={t("Moves")} value={progressPercent} />
      </>
    );
  }

  function renderOpeningActions() {
    const hasSavedProgress =
      practiceLineProgress.some(Boolean) || challengeLineProgress.some(Boolean);
    const goToChallenges =
      allPracticeLinesCompleted && hasChallenge && !allChallengeLinesCompleted;
    return (
      <div className="flex w-full flex-col gap-3">
        <Button
          className={ctaButtonClassName}
          onClick={
            goToChallenges
              ? startChallengeFromOpening
              : startPracticeFromOpening
          }
        >
          {t(hasSavedProgress ? "Continue Lesson" : "Start learning")}
        </Button>
      </div>
    );
  }

  function renderLineSelector(buttonClassName: string, align: "start" | "end") {
    if (lineCountTotal <= 0) {
      return null;
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className={buttonClassName}>
            <span>
              {t("Line")} {safeLineIndex + 1}/{lineCountTotal}
            </span>
            <FaChevronDown className="text-[11px]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={align}
          className="overflow-y-auto max-h-64 min-w-[9rem]"
        >
          {Array.from({ length: lineCountTotal }, (_, optionIndex) => (
            <DropdownMenuItem
              key={`line-${optionIndex}`}
              className={cn(
                optionIndex === safeLineIndex &&
                  "bg-athens-gray-100 font-semibold text-altblue-500",
              )}
              onSelect={() => changeLine(optionIndex)}
            >
              {t("Line")} {optionIndex + 1}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  function renderInProgressActions() {
    return (
      <Button
        variant="secondary"
        className={lessonHintClassName}
        onClick={() => {
          const key = moves[moveIndex]?.from;
          if (key) setHighLightKey(key);
        }}
        disabled={
          opponentTurn || (isManualNavigation && moveIndex < maxMoveIndex)
        }
      >
        {t("Hint")}
      </Button>
    );
  }

  function renderMoveControls() {
    return (
      <>
        <div className="flex items-center gap-1">
          <Button
            variant="quiet"
            size="icon"
            disabled={isOpening || moveIndex === 0}
            aria-label={t("lessonLibrary.previousMove")}
            title={t("lessonLibrary.previousMove")}
            onClick={() => {
              if (moveIndex > 0) {
                setIsManualNavigation(true);
                setMoveStatus(null);
                setMoveIndex((index) => index - 1);
              }
            }}
          >
            <FaStepBackward aria-hidden="true" />
          </Button>
          <Button
            variant="quiet"
            size="icon"
            disabled={isOpening || moveIndex === maxMoveIndex}
            aria-label={t("lessonLibrary.nextMove")}
            title={t("lessonLibrary.nextMove")}
            onClick={() => {
              if (moveIndex < maxMoveIndex) {
                setIsManualNavigation(true);
                setMoveStatus(null);
                setMoveIndex((index) => index + 1);
              }
            }}
          >
            <FaStepForward aria-hidden="true" />
          </Button>
        </div>
        <Button
          variant="quiet"
          size="icon"
          disabled={isOpening}
          aria-label={t("Restart")}
          title={t("Restart")}
          onClick={() => resetToOpening(startMode)}
        >
          <FaUndo aria-hidden="true" />
        </Button>
      </>
    );
  }

  function renderDoneActions() {
    const next = findNextIncompleteLine(
      effectivePracticeLineProgress,
      practiceLineMoveTotals,
    );
    if (next >= 0)
      return (
        <Button
          className={ctaButtonClassName}
          onClick={() =>
            startLesson(next, getResumableMovesForLine("practice", next))
          }
        >
          {t("Start next line")}
        </Button>
      );
    if (hasChallenge)
      return (
        <Button
          className={ctaButtonClassName}
          onClick={() => startChallenge(getNextUncompletedChallengeLine())}
        >
          {t("Start Challenge")}
        </Button>
      );
    return (
      <Button asChild className={ctaButtonClassName}>
        <Link
          href={
            query.source === "learning"
              ? pathReturnHref(lessonId)
              : "/learn/openings"
          }
        >
          {t(
            query.source === "learning"
              ? "learning.backToOverview"
              : "Back to Lessons",
          )}
        </Link>
      </Button>
    );
  }

  function renderChallengeDoneActions() {
    const next = findNextIncompleteLine(
      effectiveChallengeLineProgress,
      challengeLineMoveTotals,
    );
    if (next >= 0)
      return (
        <Button
          className={ctaButtonClassName}
          onClick={() =>
            startChallenge(next, getResumableMovesForLine("challenge", next))
          }
        >
          {t("Next challenge line")}
        </Button>
      );
    return (
      <div className="flex w-full flex-col items-start gap-3">
        <Button asChild className={ctaButtonClassName}>
          <Link
            href={
              query.source === "learning"
                ? pathReturnHref(lessonId)
                : "/learn/openings"
            }
          >
            {t(
              query.source === "learning"
                ? "learning.backToOverview"
                : "Back to Lessons",
            )}
          </Link>
        </Button>
      </div>
    );
  }

  function renderStateActions() {
    if (isOpening) {
      return renderOpeningActions();
    }

    if (isInProgressState) {
      return renderInProgressActions();
    }

    return null;
  }

  return (
    <div className={`mt-4 ${lessonWorkspaceClassName}`}>
      <LessonBoardFrame size={boardSize}>
        <Board
          lastMove={lastMove}
          settings={settings}
          fen={fen}
          animate={boardAnimate}
          autoCastle
          size={boardSize}
          playingWhite={playingWhite}
          possibleMoves={possibleMoves}
          onMoveMade={(move) => moveMade(move)}
          highLightKey={highLightKey}
        />
      </LessonBoardFrame>

      <div className={lessonPanelClassName} style={lessonPanelStyle(boardSize)}>
        <LessonPanelHeader
          title={title}
          backHref={
            query.source === "learning"
              ? pathReturnHref(
                  allPracticeLinesCompleted &&
                    (!hasChallenge || allChallengeLinesCompleted)
                    ? lessonId
                    : undefined,
                )
              : "/learn/openings"
          }
          backLabel={
            query.source === "learning"
              ? t("learning.backToOverview")
              : t("lessonLibrary.back", { subject: t("Openings") })
          }
          trailing={renderLineSelector(
            "inline-flex shrink-0 items-center gap-1 rounded px-1 py-0.5 text-xs transition-colors hover:bg-altblue-600",
            "end",
          )}
        />
        <div className="min-h-0 flex-1 p-3 md:p-4">
          {renderChallengeFeedbackCard() || renderCommentBubble()}
        </div>
        <LessonPanelFooter
          progress={renderProgressBar()}
          primary={
            lessonState === LessonState.DONE
              ? renderDoneActions()
              : lessonState === LessonState.CHALLENGE_DONE
                ? renderChallengeDoneActions()
                : renderStateActions()
          }
        >
          {renderMoveControls()}
        </LessonPanelFooter>
      </div>
    </div>
  );
}
